// frontend/src/pages/DataWorkspacePage/CommitPanel.jsx
/**
 * CommitPanel — slide-over panel that shows row-level diffs
 * and lets the user choose their write-back strategy before applying.
 *
 * Triggered by GridEditor when unsaved changes exist.
 */

import React, { useState } from 'react';
import { apiFetch } from '../../api/index.js';
import { useNotification } from '../../context/NotificationContext.jsx';
import { DynamicIcon } from '../../components/icons.jsx';

const STRATEGIES = [
    { value: 'UPDATE', label: 'UPDATE', description: 'Overwrite existing rows only', icon: 'Pencil' },
    { value: 'INSERT', label: 'INSERT', description: 'Insert as new rows',           icon: 'Plus'   },
    { value: 'UPSERT', label: 'UPSERT', description: 'Insert or update (merge)',     icon: 'GitMerge' },
];

const CommitPanel = ({ token, connectionId, tableName, schemaName, diffs, onClose, onApplied }) => {
    const { addNotification } = useNotification();
    const [strategy, setStrategy] = useState('UPDATE');
    const [applying, setApplying] = useState(false);
    const [staged, setStaged]     = useState(null);   // staged commit from backend

    // -----------------------------------------------------------------------
    // Stage the diffs (preview)
    // -----------------------------------------------------------------------
    const handleStage = async () => {
        try {
            const res = await apiFetch('/data-workspace/commits/stage', token, {
                method: 'POST',
                body: JSON.stringify({
                    connection_id: connectionId,
                    table_name:    tableName,
                    schema_name:   schemaName || null,
                    strategy,
                    diffs: diffs.map(d => ({ original: d.original, modified: d.modified })),
                }),
            });
            setStaged(res);
        } catch (err) {
            addNotification(`Staging failed: ${err.message}`, 'error');
        }
    };

    // -----------------------------------------------------------------------
    // Apply staged commit
    // -----------------------------------------------------------------------
    const handleApply = async () => {
        if (!staged?.id) { await handleStage(); return; }
        setApplying(true);
        try {
            const res = await apiFetch(`/data-workspace/commits/${staged.id}/apply`, token, { method: 'POST' });
            addNotification(`${res.rows_affected} row(s) written to ${tableName} ✓`, 'success');
            onApplied();
        } catch (err) {
            addNotification(`Apply failed: ${err.message}`, 'error');
        } finally {
            setApplying(false);
        }
    };

    return (
        <div className="absolute inset-y-0 right-0 w-[480px] flex flex-col bg-[#0a0f1e] border-l border-white/10 shadow-2xl z-30">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                    <DynamicIcon name="GitCommit" className="w-5 h-5 text-amber-400" />
                    <h3 className="font-semibold text-slate-100">Commit Changes</h3>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30">
                        {diffs.length} {diffs.length === 1 ? 'row' : 'rows'}
                    </span>
                </div>
                <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/5 text-slate-500 hover:text-slate-300 transition-colors">
                    <DynamicIcon name="X" className="w-4 h-4" />
                </button>
            </div>

            {/* Strategy selector */}
            <div className="px-5 py-4 border-b border-white/10 space-y-3">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Write-back Strategy</p>
                <div className="space-y-2">
                    {STRATEGIES.map(s => (
                        <button
                            key={s.value}
                            onClick={() => { setStrategy(s.value); setStaged(null); }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${
                                strategy === s.value
                                    ? 'bg-blue-600/20 border-blue-500/50 text-blue-300'
                                    : 'bg-white/[0.03] border-white/10 text-slate-400 hover:border-white/20'
                            }`}
                        >
                            <div className={`p-1.5 rounded-lg ${strategy === s.value ? 'bg-blue-600/30' : 'bg-white/5'}`}>
                                <DynamicIcon name={s.icon} className="w-4 h-4" />
                            </div>
                            <div>
                                <p className="text-sm font-medium">{s.label}</p>
                                <p className="text-xs opacity-60">{s.description}</p>
                            </div>
                            {strategy === s.value && <DynamicIcon name="Check" className="w-4 h-4 ml-auto text-blue-400" />}
                        </button>
                    ))}
                </div>
            </div>

            {/* Diff viewer */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Row-level Diff</p>
                {diffs.map((diff, idx) => (
                    <DiffCard key={idx} diff={diff} idx={idx} />
                ))}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-white/10 flex items-center gap-3">
                {!staged ? (
                    <button
                        onClick={handleStage}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors"
                    >
                        <DynamicIcon name="Eye" className="w-4 h-4" />
                        Preview Commit
                    </button>
                ) : (
                    <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-900/20 border border-emerald-500/30 text-xs text-emerald-400">
                        <DynamicIcon name="CheckCircle" className="w-4 h-4 shrink-0" />
                        Staged — {staged.diffs?.length || diffs.length} row(s) ready to apply
                    </div>
                )}
                <button
                    onClick={handleApply}
                    disabled={applying}
                    className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <DynamicIcon name={applying ? 'Loader2' : 'Zap'} className={`w-4 h-4 ${applying ? 'animate-spin' : ''}`} />
                    {staged ? 'Apply Now' : 'Stage & Apply'}
                </button>
            </div>
        </div>
    );
};

// --------------------------------------------------------------------------
// Single-row diff card
// --------------------------------------------------------------------------
const DiffCard = ({ diff, idx }) => {
    const changedCols = Object.keys(diff.modified).filter(k =>
        diff.original[k] !== diff.modified[k]
    );

    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <div className="px-3 py-2 bg-white/[0.03] border-b border-white/10 flex items-center gap-2">
                <span className="text-xs text-slate-500">Row #{idx + 1}</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-400 border border-amber-800/30">
                    {changedCols.length} {changedCols.length === 1 ? 'change' : 'changes'}
                </span>
            </div>
            <div className="divide-y divide-white/5">
                {changedCols.map(col => (
                    <div key={col} className="px-3 py-2">
                        <p className="text-[10px] text-slate-600 font-mono mb-1">{col}</p>
                        <div className="flex items-center gap-2 text-xs">
                            <span className="px-2 py-0.5 rounded bg-red-900/30 text-red-400 font-mono line-through max-w-[180px] truncate">
                                {String(diff.original[col] ?? 'NULL')}
                            </span>
                            <DynamicIcon name="ArrowRight" className="w-3 h-3 text-slate-600 shrink-0" />
                            <span className="px-2 py-0.5 rounded bg-emerald-900/30 text-emerald-400 font-mono max-w-[180px] truncate">
                                {String(diff.modified[col] ?? 'NULL')}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CommitPanel;
